import { AfterViewInit, Component, computed, EventEmitter, input, Input, OnInit, Output, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { CreateMode, DisplayMode, GridComponent, IGridColumn, IGridSettings, ITableSettings, UpdateMode } from "@features/grid";
import { Gender, GenderMap, Organization, PatientLogin, UserTitle, UserTitleMap } from "@main/users";
import { UserHelperService } from "@main/users/user-helper.service";
import { ConfirmationService, MessageService } from "primeng/api";
import { DuplicateCheckerType, DuplicatePatient, DuplicatePatientCheckResult } from "./duplicate-patient";
import { EditPatientInfo } from "./patient-page-item";
import { PlayerService } from "./player.service";
import { Permissions } from "@main/permissions/permissions";
import { WalkInService } from "@project/walk-ins/classes/walk-in.service";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { SquadType } from "@project/models/squad";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { CancelDuplicatePatientsDialogInfo } from "./cancel-duplicate-patients-dialog-info";
import { MergePatientProcessingEndpoint, UserMergePatientEndpoint } from "@project/merge-patient/services/merge-processing.service";
import { OrganizationService } from "@main/companies";
import { OrganizationGroup } from "@mipro/custom-controls/organization-group";
import { Message } from "@bbwt/classes";

interface DuplicatePatientWithStrength extends DuplicatePatient {
    matchStrength: "Strong match" | "Weak match";
}
@Component({
    selector: "duplicate-patients-dialog",
    templateUrl: "./duplicate-patients-dialog.component.html",
    styleUrls: ["./duplicate-patients-dialog.component.scss"],
    providers: [
        {
            provide: MergePatientProcessingEndpoint,
            useValue: UserMergePatientEndpoint
        }
    ]
})

export class DuplicatePatientsDialogComponent implements OnInit, AfterViewInit{
    private _duplicates: DuplicatePatientCheckResult;
    private _visible = false;
    private _allowSelectExistingPatient = true;
    _currentPatient: PatientLogin;
    public GenderMap = GenderMap;
    public UserTitleMap = UserTitleMap;
    public loadingOrgs = false;

    private currentUserAccessibleSquads: { [id: string]: { squadName: string; orgName: string } } = {};

    protected organizationGroupsIds = input<string[]>();

    @ViewChild("matchesGrid", { static: true }) duplicatesGrid: GridComponent;
    @ViewChild("fuzzyMatchesGrid", { static: true }) fuzzyMatchesGrid: GridComponent;

    showMatchesSection: boolean = false;
    showFuzzyMatchesSection: boolean = false;
    protected exactMatchReason: "Email" | "NHS Number";
    combinedDuplicates: DuplicatePatientWithStrength[] = [];
    public mode: "create" | "select" = "select";

    protected groupsNames = computed<string[]>(() =>
        this.organizationGroupsIds()
            ?.filter(id => !!this.currentUserAccessibleSquads[id])
             .map(id => `${this.currentUserAccessibleSquads[id].squadName} (${this.currentUserAccessibleSquads[id].orgName})`)
            ?? []
    )

    protected firstGroupName = computed<string>(() =>
        this.groupsNames()?.length > 0 ? this.truncateGroupName(this.groupsNames()[0]) : null
    );

    @Input() set duplicates(d: DuplicatePatientCheckResult) {
        this.showMatchesSection = false;
        this.showFuzzyMatchesSection = false;

        this.combinedDuplicates = [];

        if (d?.matches?.length) {
          this.combinedDuplicates.push(
            ...d.matches.map(x => ({ ...x, matchStrength: "Strong match" as const }))
          );
        }
        if (d?.fuzzyNameMatches?.length) {
          this.combinedDuplicates.push(
            ...d.fuzzyNameMatches.map(x => ({ ...x, matchStrength: "Weak match" as const }))
          );
        }

        this.showMatchesSection = this.combinedDuplicates.length > 0;

        this.combinedDuplicates.forEach(
            d => {
                d.organizationGroups = d.organizationGroups.filter(groupId => !!this.currentUserAccessibleSquads[groupId])
        });

        this.loadDuplicatePatientsGrid();
      }

      @Input() set currentPatient(login: PatientLogin) {
        this._currentPatient = login;
        this.editing = !!(login?.id || login?.patientId);
        if (login) {
            this.editing = !!(login.id || login.patientId);
        }
    }

    async ngOnInit() {
        await this.initOrganizationGroups();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            const buttons = document.querySelectorAll("button.select-link");

            buttons.forEach(btn => {
            const el = btn as HTMLElement;
            el.style.background = "transparent";
            el.style.border = "none";
            el.style.boxShadow = "none";
            el.style.color = "#023648";
            el.style.fontSize = "14px";
            el.style.fontWeight = "500";
            el.style.padding = "0 8px";
            el.style.height = "32px";
            el.style.display = "flex";
            el.style.justifyContent = "center";
            el.style.alignItems = "center";
            el.style.gap = "5px";
            });
        }, 0);
    }

    emailMatches: boolean = false;
    exactMatches: boolean = false;

    @Input() selfRegWalkInId: string;
    @Input() selfRegistrationUrl: string;
    @Input() inputsGroupStyle: any;

    @Input() set visible(visible: boolean) {
        this._visible = visible;
        if (visible) {
            this.confirmingInitialAction = false;
        }
        this.visibleChange.emit(visible);
    }
    get visible() {
        return this._visible;
    }

    @Input() set allowSelectExistingPatient(value: boolean) {
        this._allowSelectExistingPatient = value;
        this.gridSettings.selectColumn = value;
    }
    get allowSelectExistingPatient() {
        return this._allowSelectExistingPatient;
    }

    get detailCells() {
    return [
        { label: "First name", value: this._currentPatient.firstName },
        { label: "Last name", value: this._currentPatient.lastName.toUpperCase() },
        { label: "Date of birth", value: this._currentPatient.dateOfBirth },     // <-- raw
        { label: "Sex", value: this.GenderMap[this._currentPatient.gender] },
    ];
    }

    get overflowCount(): number {
    return Math.max(0, this.groupsNames().length - 1);
    }

    get overflowTooltip(): string {
        const names = this.groupsNames();
        const rows: string[] = [];
        for (let i = 0; i < names.length; i += 2) {
            const pair = names.slice(i, i + 2).join("\n");
            rows.push(pair);
        }

        return rows.join(",\n");
    }

    mergePatientDialogVisible: boolean = false;
    mergeParentId: string;
    mergeChildId: string;

    @Input() duplicateCheckerType: DuplicateCheckerType = DuplicateCheckerType.CreateNewPatient;

    @Output() readonly visibleChange = new EventEmitter<boolean>();
    @Output() readonly completed = new EventEmitter<PatientLogin | DuplicatePatient>();
    @Output() readonly proceedInitialAction = new EventEmitter<any>();
    @Output() readonly onCancel = new EventEmitter<CancelDuplicatePatientsDialogInfo>();

    groupForm = this.fb.group({
        organizationGroup: [[], Validators.required]
    });

    gridSettings: IGridSettings = {
        updateMode: UpdateMode.Disabled,
        createMode: CreateMode.Disabled,
        deletingEnabled: false,
        selectColumn: false,
        rowExpansionEnabled: false,
        simpleViewMode: true,
        hideActionsHeader: true,
        rowStyleClassFunc: (rowData: DuplicatePatient) => ({ "row-archived-patient": rowData.isArchived }),
        additionalRowActions: [
            {
              label: "Select ›",
            buttonClass: "select-link p-button-text p-button-plain",
              visible: () => true,
              handler: (row: DuplicatePatientWithStrength) => {
                this.selectedPatient = row;
              }
            }
        ]
    };

    tableSettings: ITableSettings = {
        columns: <IGridColumn[]>[
            {
                field: "matchStrength",
                header: "Match type",
                bodyStyleFunction: (_r, v) =>
                    v === "Strong match"
                    ? {
                        "background-color": "#d3ebd4",
                        color: "#327036",
                        "border-radius": "6px",
                        height: "27px",
                        padding: "3px 8px",
                        display: "flex",
                        "justify-content": "center",
                        "align-items": "center",
                        "max-width": "120px",
                        "font-size": "14px",
                        "font-weight": "400",
                        "line-height": "150%"
                        }
                    : {
                        "background-color": "#FFF0C2",
                        color: "#7F6003",
                        "border-radius": "6px",
                        height: "27px",
                        padding: "3px 8px",
                        display: "flex",
                        "justify-content": "center",
                        "align-items": "center",
                        "max-width": "120px",
                        "font-size": "14px",
                        "font-weight": "400",
                        "line-height": "150%"
                    }
            },
            { field: "firstName", header: "First name" },
            { field: "lastName", header: "Last name" },
            { field: "dateOfBirth", header: "Date Of Birth", displayMode: DisplayMode.Date, displayDateMomentFormat: "DD/MM/YYYY" },
            { field: "gender", header: "Sex", displayHandler: (gender: Gender) => GenderMap[gender] },
            { field: "postalCode", header: "Postcode" },
            {
                field: "organizationGroups",
                header: this.namingConvService.groupPlural,
                showPlus: true,
                plusLimit: 1,
                plusTooltip: true,
                plusValueGetter: (squadId: string) => {
                    const squad = this.currentUserAccessibleSquads[squadId];
                    return squad ? `${squad.squadName} (${squad.orgName})` : squadId;
                }
            }
        ],
        value: [],
        selectionMode: "single",
        dataKey: "id",
        sortField: "firstName",
        rowExpandMode: "single",
        lazy: false
    };

    selectedPatient: DuplicatePatient;
    selectedEditInfo: EditPatientInfo;
    editing?: boolean = null;
    confirmingInitialAction = false;
    usingSelectedPatient = false;

    saving: boolean = false;

    get confirmInitialActionButtonLabel(): string {
        return (this.duplicateCheckerTypeMergePatient
                    ? "Approve "
                    :  this.editing && !this._currentPatient.selfRegister
                            ? "Update "
                            : "Add ")
                + this.namingConvService.endUserSingular;

    }

    get continueWithSelectedPatientButtonLabel(): string {
        return "Select Existing " +
               this.namingConvService.endUserSingular +
               (this.duplicateCheckerTypeMergePatient ? " and Merge" : " and Continue");
    }

    get duplicateCheckerTypeMergePatient(): boolean {
        return this.duplicateCheckerType == DuplicateCheckerType.MergePatients;
    }

    constructor(
        private fb: UntypedFormBuilder,
        private playerService: PlayerService,
        private patientService: PatientService,
        private walkInService: WalkInService,
        private confirmationService: ConfirmationService,
        private router: Router,
        private userHelperService: UserHelperService,
        private messageService: MessageService,
        public namingConvService: NamingConventionService,
    ) {}

    private truncateGroupName(groupName: string): string {
        return groupName.length > 22
                ? groupName.slice(0, 19) + "..."
                : groupName;
    }

    private async initOrganizationGroups() {
        let orgGroups: OrganizationGroup[];
        if (this.selfRegistrationUrl) {
            const orgGroup = await this.playerService.getOrganizationGroupsBySelfRegistrationUrl(this.selfRegistrationUrl);
            orgGroups = orgGroup ? [orgGroup] : [];
        } else {
            orgGroups = await this.playerService.getOrganizationGroups();
        }
        this.currentUserAccessibleSquads = this.buildSquadsArray(orgGroups);
    }

    private buildSquadsArray(orgGroups: OrganizationGroup[]): { [id: string]: { squadName: string; orgName: string } } {
        if (orgGroups.length) {
            const result = {};
            orgGroups.forEach(og =>
                og.groups
                    .filter(g => g.type !== SquadType.UnassignedPatients && !g.archived)
                    .forEach(g => (result[g.id] = { squadName: g.name, orgName: og.name }))
            );
            return result;
        } else return {};
    }

    private close() {
        this.visible = false;
    }

    protected cancel() {
        const cancelInfo = <CancelDuplicatePatientsDialogInfo>{
            exactMatchByNhsNumber: !!this.exactMatches && this.exactMatchReason === "NHS Number"
        };
        this.onCancel.emit(cancelInfo);
        this.close();
    }

    confirmInitialAction() {
        if (!this._currentPatient) {
            return;
        }

        const message = this._currentPatient.selfRegister
            ? `Are you sure you want to Approve Self Registered ${this.namingConvService.endUserSingular}?`
            : this.editing
            ? `Are you sure you want to update the ${this.namingConvService.endUserSingular}?`
            : `Are you sure you want to create a New ${this.namingConvService.endUserSingular}?`;

        this.confirmationService.confirm({
            message,
            accept: async () => {
                this.confirmingInitialAction = true;
                try {
                    this.saving = true;
                    if (this.selfRegWalkInId) {
                        try {
                            await this.playerService.approveSelfRegisterPlayer(this._currentPatient.patientId);
                        } finally {
                            this.patientService.triggerPatientsUpdated(this._currentPatient.patientId);
                            this.terminate(this._currentPatient);
                            this.confirmingInitialAction = false;
                            return;
                        }
                    } else if (!this.allowSelectExistingPatient) {
                        this.confirmingInitialAction = true;
                        this.proceedInitialAction.emit();
                        return;
                    } else {
                        try {
                            const confirmAction = this.editing
                                ? this.playerService.update(this._currentPatient.id, this._currentPatient, true)
                                : this.playerService.create(this._currentPatient, true);
                            const patient = (await confirmAction) as PatientLogin;
                            this.terminate(patient);
                            this.confirmingInitialAction = false;
                            this.saving = false;
                        } catch(ex){
                            this.messageService.add(Message.Error(ex.error));
                        }
                    }
                } finally {
                    this.saving = false;
                }
            }
        });
    }

    async continueWithSelectedPatient() {
        this.usingSelectedPatient = true;
        this.saving = true;
        this.onDialogClosed();
        try {
            const userId = this.selectedEditInfo?.id || (this.selectedPatient.editInfo && this.selectedPatient.editInfo[0]?.id);
            if (this.selfRegWalkInId) {
                var currentWalkIn = await this.walkInService.get(this.selfRegWalkInId);
                currentWalkIn.patientId = this.selectedPatient.id;
                currentWalkIn.isCreatedBySRExisted = true;
                await this.walkInService.update(currentWalkIn.id, currentWalkIn, { showSuccessMessage: false });

                if (this.duplicateCheckerType == DuplicateCheckerType.CreateNewPatient) {
                    await this.playerService.deleteSelfReg(this._currentPatient.patientId, { showSuccessMessage: false });

                    this.patientService.triggerPatientsUpdated(this.selectedPatient.id);
                    this.terminate(this.selectedPatient);
                    return;
                }

                if (this.duplicateCheckerType == DuplicateCheckerType.MergePatients) {
                    this.mergeParentId = this._currentPatient.patientId;
                    this.mergeChildId = this.selectedPatient.id;
                    this.mergePatientDialogVisible = true;
                    this.close();
                    return;
                }
            }

            await this.playerService.addGroupsToPatient(this.selectedPatient.id, this._currentPatient.organizationGroups);

            this.terminate(this.selectedPatient);

            if (!this.userHelperService.hasPermission(Permissions.ReadPatient)) {
                return;
            }

            this.patientService.triggerPatientsUpdated(this.selectedPatient.id);

            var queryParams = {};

            if (!!userId) {
                this.router.navigate(["/app/patients/edit", userId], { queryParams });
            } else {
                queryParams["profileId"] = this.selectedPatient.id;
                this.router.navigate(["/app/patients/edit/0"], { queryParams });
            }
        } finally {
            this.usingSelectedPatient = false;
            this.saving = false;
        }
    }

    private terminate(patient?: PatientLogin | DuplicatePatient) {
        this.completed.emit(patient);
        this.close();
    }

    public readonly SquadType = SquadType;

   private loadDuplicatePatientsGrid() {
        const list = this.combinedDuplicates;
        this.tableSettings = {
            ...this.tableSettings,
            value: list,
            paginator: false,
            rows: list.length,
            rowsPerPageOptions: []
        };
    }

    goToLoginPage() {
        this.router.navigateByUrl("/account/login");
    }

    onMergePatientDialogClose(parentId: string) {
        const parentSelected = this.selectedPatient.id == parentId ? this.selectedPatient : this._currentPatient;
        this.patientService.triggerPatientsUpdated(parentId);
        this.terminate(parentSelected);
    }

    onModeChange(mode: "create" | "select") {
        this.mode = mode;
        this.allowSelectExistingPatient = (mode === "create");

        this.gridSettings.selectColumn = (mode === "select");

        this.tableSettings = {
            ...this.tableSettings,
            selectionMode: mode === "select" ? "single" : undefined
        };

        if (mode === "create") {
            this.selectedPatient = null;
        }
    }

    canContinue() {
        return (this.mode === "create" || !!this.selectedPatient) && !this.saving;
    }

    continue() {
        if (this.mode === "create") {
            this.confirmInitialAction();
        } else {
            this.continueWithSelectedPatient();
        }
    }

    onDialogClosed() {
        document.body.classList.remove("p-overflow-hidden");
        document.body.style.removeProperty("--scrollbar-width");
    }
}
